import React, { useEffect, useState } from 'react';
import Icon from './icons';
import { brandPrefix } from '../brand';
import { officeOf } from './types';
import { sendCollectNotification } from './api';
import type { NotifyRecipient } from './api';
import type { Project, PersonCard, SendLog, SendRecip } from './types';

interface Props {
  p: Project;
  card: PersonCard;
  portalUrl: string;
  collectProjectId: string;
  onClose: () => void;
  onSend: (log: SendLog) => void;
}

interface Row { checked: boolean; name: string; tel: string; email: string; type: 'A' | 'B'; }

const CHANNELS = ['SMS', '이메일', 'SMS + 이메일'];
// 발신번호는 계정 설정(sender_nums)에서 서버가 고른다 — 특정 번호를 여기 박아 두면 다른 계정·브랜드에서도 그 번호로 나간다
const FROMS: [string, string][] = [['', '계정 발신번호 (자동)']];

export default function SendModal({ p, card, portalUrl, collectProjectId, onClose, onSend }: Props) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const [rows, setRows] = useState<Row[]>(() => card.recips.map((r) => ({
    checked: !!(r.tel || r.email),
    name: r.name, tel: r.tel, email: r.email,
    type: (r.role || '').includes('FP') || (r.role || '').includes('소개') ? 'B' : 'A',
  })));
  const [channel, setChannel] = useState(CHANNELS[0]);
  const [from, setFrom] = useState(FROMS[0][0]);
  const [editing, setEditing] = useState(false);
  const who = card.name || 'GP';
  const [msgA, setMsgA] = useState(
    `${brandPrefix(officeOf(p))} ${p.org} 자료 제출 안내\n\n안녕하세요.\n${p.org} 결성 진행을 위해 필요한 자료를 안내드립니다.\n\n아래 링크에서 항목을 확인하시고 제출 부탁드립니다.\n${portalUrl}`
  );
  const [msgB, setMsgB] = useState(
    `${brandPrefix(officeOf(p))} ${p.org} 자료 수집 안내\n\n안녕하세요.\n${who}님께 ${p.org} 결성에 필요한 자료 제출을 요청드렸습니다.\n\n아래 링크에서 제출 현황을 실시간 확인하실 수 있으며, 보유하신 자료가 있으시면 대신 제출도 가능합니다.\n${portalUrl}`
  );

  const upd = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const selCount = rows.filter((r) => r.checked).length;
  const [sending, setSending] = useState(false);
  const channelKey = (): 'sms' | 'email' | 'both' => (channel === 'SMS' ? 'sms' : channel === '이메일' ? 'email' : 'both');

  const fire = async () => {
    if (!selCount || sending) return;
    setSending(true);
    const ch = channelKey();
    const now = new Date();
    const at = now.getMonth() + 1 + '/' + now.getDate() + ' ' + now.toTimeString().slice(0, 5);
    const chosen = rows.filter((r) => r.checked);
    const out: SendRecip[] = [];
    const toRecipients = (g: Row[]): NotifyRecipient[] => g.map((r) => ({ phone: r.tel, email: r.email, name: r.name || '(이름 없음)', msgType: r.type }));
    const run = async (g: Row[], msg: string) => {
      if (!g.length) return;
      const resp = await sendCollectNotification({
        projectId: collectProjectId, type: 'collect',
        recipients: toRecipients(g), channel: ch, senderNum: from || undefined,
        messageSms: msg, messageEmail: msg,
      });
      g.forEach((r) => {
        const rr = resp.results.find((x) => (r.tel && x.phone === r.tel) || (r.email && x.email === r.email));
        const smsGood = !!(rr && rr.smsOk === true);
        const emailGood = !!(rr && rr.emailOk === true);
        const sentOk = ch === 'sms' ? smsGood : ch === 'email' ? emailGood : (smsGood || emailGood);
        out.push({
          name: r.name || '(이름 없음)', tel: r.tel, email: r.email, type: r.type,
          status: sentOk ? '성공' : '실패',
          reason: sentOk ? '' : (rr ? (rr.smsMsg || rr.emailMsg || '발송 실패') : ((r.tel || r.email) ? '발송 실패' : '연락처 없음')),
        });
      });
    };
    try {
      await run(chosen.filter((r) => r.type === 'A'), msgA);
      await run(chosen.filter((r) => r.type === 'B'), msgB);
      onSend({ at, channel, recips: out });
    } catch (e) {
      onSend({ at, channel, recips: chosen.map((r) => ({ name: r.name || '(이름 없음)', tel: r.tel, email: r.email, type: r.type, status: '실패', reason: (e as Error).message || '발송 오류' })) });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ovm">
      <div className="ovbox" style={{ maxWidth: 620, width: '94%' }}>
        <div className="ovh"><b>📨 자료요청 전송 · {who}</b><button className="povx" onClick={onClose}><Icon n="x" /> 닫기</button></div>

        <div style={{ maxHeight: '64vh', overflowY: 'auto', padding: '4px 2px 2px' }}>
          <div className="smcard">
            <div className="smttl">1. 수신자 <span style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 11.5 }}>· A형 = 대표/담당자 제출 요청 · B형 = 소개자/FP 참고 안내</span></div>
            {rows.map((r, i) => (
              <div className="smrow" key={i}>
                <input type="checkbox" checked={r.checked} onChange={(e) => upd(i, { checked: e.target.checked })} />
                <input className="rcname" style={{ flex: '0 0 120px' }} value={r.name} onChange={(e) => upd(i, { name: e.target.value })} placeholder="이름" />
                <input style={{ flex: 1, minWidth: 0 }} value={r.tel} onChange={(e) => upd(i, { tel: e.target.value })} placeholder="휴대폰" />
                <select className="itype" style={{ flex: '0 0 84px' }} value={r.type} onChange={(e) => upd(i, { type: e.target.value as 'A' | 'B' })}>
                  <option value="A">A형</option><option value="B">B형</option>
                </select>
              </div>
            ))}
            <button className="cact" onClick={() => setRows((rs) => [...rs, { checked: true, name: '', tel: '', email: '', type: 'A' }])}>＋ 수신자 추가</button>
          </div>

          <div className="smcard">
            <div className="smttl">2. 발송 채널 · 발신번호</div>
            <div className="smchan">
              {CHANNELS.map((c) => (
                <button key={c} className={'smchip' + (channel === c ? ' on' : '')} onClick={() => setChannel(c)}>{c}</button>
              ))}
              <select className="itype" style={{ marginLeft: 'auto', flex: '0 0 170px' }} value={from} onChange={(e) => setFrom(e.target.value)}>
                {FROMS.map(([v, l]) => <option key={v || 'auto'} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          <div className="smcard" style={{ marginBottom: 4 }}>
            <div className="smttl" style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ flex: 1 }}>3. 메시지 미리보기</span>
              <button className="smedit" onClick={() => setEditing((v) => !v)}>{editing ? '✓ 수정 완료' : '✏ 직접 수정'}</button>
            </div>
            <div className="smlab">A형 (대표자/담당자)</div>
            {editing
              ? <textarea className="idesc" style={{ minHeight: 130 }} value={msgA} onChange={(e) => setMsgA(e.target.value)} />
              : <div className="smprev">{msgA}</div>}
            <div className="smlab">B형 (소개자/FP)</div>
            {editing
              ? <textarea className="idesc" style={{ minHeight: 130 }} value={msgB} onChange={(e) => setMsgB(e.target.value)} />
              : <div className="smprev b">{msgB}</div>}
            <div className="snote" style={{ marginTop: 8 }}>전송하면 고객에게 실제 문자/이메일이 발송되고, 자료수집 모듈에 포털이 등록됩니다.</div>
          </div>
        </div>

        <button className="sendbtn" onClick={fire} disabled={!selCount || sending}>
          <Icon n="send" style={{ width: 14, height: 14 }} /> {sending ? '전송 중…' : selCount + '명에게 전송'}
        </button>
      </div>
    </div>
  );
}
